// src/pages/packages/pages/PackageCustomersPage.jsx

import "./PackageCustomersPage.scss";

import { Badge, Col, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import {
  faBan,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faSearch,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import ActionPillButton from "../../../../../commons/buttons/ActionPillButton";
import AppInputV2 from "../../../../../commons/inputs/AppInputV2";
import AppLoading from "../../../../../commons/AppLoading";
import ConfirmModal from "../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../commons/cards/Card";
import EmptyState from "../../../../../commons/EmptyState";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../../../../commons/HallAppToast";
import MenuTitle from "../../../../../commons/menu/MenuTitle";
import PackageSubscriptionAPI from "../../api/PackageSubscriptionAPI";
import PackageUsageBar from "../../componentes/PackageUsageBar";
import ReportHeader from "../../../../report-full/components/ReportHeader";
import { useParams } from "react-router-dom";

const api = new PackageSubscriptionAPI();

function formatDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("pt-BR");
  } catch {
    return "—";
  }
}

function statusLabel(v) {
  switch (v) {
    case "ACTIVE":
      return "Ativo";
    case "EXPIRED":
      return "Expirado";
    case "CANCELED":
      return "Cancelado";
    case "FINISHED":
      return "Finalizado";
    default:
      return v || "—";
  }
}

function statusVariant(v) {
  switch (v) {
    case "ACTIVE":
      return "success";
    case "EXPIRED":
      return "secondary";
    case "CANCELED":
      return "danger";
    case "FINISHED":
      return "primary";
    default:
      return "light";
  }
}

function CustomerPackageCard({ it, onAskCancel }) {
  const soldAt = it.soldAt || it.createdAt;
  const expiresAt = it.expiresAt;
  const canCancel = it.saleStatus === "ACTIVE";

  const packageItems = useMemo(() => {
    if (!Array.isArray(it.items)) return [];

    return it.items.map((item) => ({
      ...item,
      quantityTotal: Number(item.quantityTotal) || 0,
      quantityUsed:
        item.quantityUsed != null
          ? Number(item.quantityUsed) || 0
          : Math.max(
              (Number(item.quantityTotal) || 0) -
                (Number(item.quantityRemaining) || 0),
              0
            ),
      quantityRemaining:
        item.quantityRemaining != null
          ? Number(item.quantityRemaining) || 0
          : Math.max(
              (Number(item.quantityTotal) || 0) -
                (Number(item.quantityUsed) || 0),
              0
            ),
    }));
  }, [it.items]);

  return (
    <CustomCard>
      <div className="package-customer-card">
        <div className="package-customer-card__header">
          <div>
            <div className="package-customer-card__name">
              {it.customerName || `Cliente #${it.customerId}`}
            </div>

            <div className="package-customer-card__sub">
              {it.customerWhatsapp || "Sem WhatsApp"}
            </div>
          </div>

          <Badge bg={statusVariant(it.saleStatus)} pill>
            <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            {statusLabel(it.saleStatus)}
          </Badge>
        </div>

        <hr />

        <div className="package-customer-card__info">
          <div className="package-info-row">
            <div className="package-info-left">
              <FontAwesomeIcon icon={faClock} />
              <span>Comprado em</span>
            </div>

            <div className="package-info-right">{formatDateTime(soldAt)}</div>
          </div>

          <div className="package-info-row">
            <div className="package-info-left">
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>Validade</span>
            </div>

            <div className="package-info-right">{formatDateTime(expiresAt)}</div>
          </div>

          <div style={{ marginTop: 14 }}>
            {!packageItems.length ? (
              <div className="package-customer-card__empty-services">
                Nenhum serviço vinculado a este pacote.
              </div>
            ) : (
              <div className="package-customer-card__services">
                {packageItems.map((item) => (
                  <div
                    key={item.id || `${item.serviceId}-${item.serviceName}`}
                    className="package-customer-card__service-item"
                  >
                    <PackageUsageBar
                      label={item.serviceName || `Serviço #${item.serviceId}`}
                      total={item.quantityTotal}
                      remaining={item.quantityRemaining}
                    />

                    <div className="package-customer-card__service-meta">
                      <span>
                        Usadas: <strong>{item.quantityUsed}</strong>
                      </span>
                      <span>
                        Restantes: <strong>{item.quantityRemaining}</strong>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {canCancel && (
          <div className="package-customer-card__actions">
            <ActionPillButton
              icon={faBan}
              label="Cancelar"
              onClick={() => onAskCancel(it.packageSaleId || it.id)}
              color="#b42318"
              borderColor="#ffd6d6"
              title="Cancelar pacote"
            />
          </div>
        )}
      </div>
    </CustomCard>
  );
}

function PackageCustomersPage() {
  const { packageId } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);

  const [search, setSearch] = useState("");

  const [showModalCancel, setShowModalCancel] = useState(false);
  const [isLoadingModalCancel, setIsLoadingModalCancel] = useState(false);
  const [pendingCancelId, setPendingCancelId] = useState(null);

  const [packageName, setPackageName] = useState("Clientes do Pacote");

  async function load() {
    try {
      setIsLoading(true);

      const res = await api.getCustomersByPackage(packageId);
      const data = res?.data || res;

      const list = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      if (list.length > 0) {
        setPackageName(list[0]?.packageName || "Clientes do Pacote");
      }

      setItems(list);
    } catch (e) {
      setItems([]);
      HallAppToast.genericError();
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [packageId]);

  const filteredItems = useMemo(() => {
    if (!search) return items;

    const s = search.toLowerCase();

    return items.filter((it) => {
      const name = it.customerName?.toLowerCase() || "";
      const phone = it.customerWhatsapp || "";
      return name.includes(s) || phone.includes(s);
    });
  }, [search, items]);

  function askCancel(id) {
    setPendingCancelId(id);
    setShowModalCancel(true);
  }

  const pendingCancelItem = useMemo(() => {
    return items.find((x) => (x.packageSaleId || x.id) === pendingCancelId);
  }, [pendingCancelId, items]);

  async function executeCancel() {
    if (!pendingCancelId) return;

    try {
      setIsLoadingModalCancel(true);

      await api.cancel(pendingCancelId);

      HallAppToast.success("Pacote cancelado com sucesso.");

      setItems((prev) =>
        prev.map((it) => {
          const id = it.packageSaleId || it.id;

          return id === pendingCancelId
            ? { ...it, saleStatus: "CANCELED" }
            : it;
        })
      );

      setShowModalCancel(false);
      setPendingCancelId(null);
    } catch {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalCancel(false);
    }
  }

  return (
    <div className="container package-customers-page">
      <ReportHeader name={packageName} />

      <MenuTitle
        title="Clientes do Pacote"
        subtitle="Clientes que compraram este pacote."
        showArrowInfo={false}
      />

      <div style={{ marginBottom: 20 }}>
        <AppInputV2
          label="Buscar cliente"
          placeholder="Digite nome ou telefone"
          value={search}
          name="search"
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<FontAwesomeIcon icon={faSearch} />}
        />
      </div>

      {isLoading ? (
        <AppLoading />
      ) : filteredItems.length === 0 ? (
        <EmptyState
          title="Nenhum cliente encontrado"
          description="Nenhum cliente corresponde à pesquisa."
          icon={<FontAwesomeIcon icon={faUsers} />}
        />
      ) : (
        <Row className="g-3" style={{ margin: 0, width: "100%" }}>
          {filteredItems.map((it) => {
            const id = it.packageSaleId || it.id;

            return (
              <Col xs={12} md={6} lg={4} key={id}>
                <CustomerPackageCard it={it} onAskCancel={askCancel} />
              </Col>
            );
          })}
        </Row>
      )}

      {showModalCancel && (
        <ConfirmModal
          show={showModalCancel}
          title="Cancelar pacote"
          isLoading={isLoadingModalCancel}
          textBtnConfirm="cancelar"
          textBtnCancel="fechar"
          confirm={executeCancel}
          closeModal={() => {
            setShowModalCancel(false);
            setPendingCancelId(null);
          }}
        >
          Deseja cancelar o pacote de{" "}
          <strong>{pendingCancelItem?.customerName || "este cliente"}</strong>?
        </ConfirmModal>
      )}
    </div>
  );
}

export default PackageCustomersPage;